import React from 'react';
import { PageHeader } from '../components/layout/PageHeader';
import { CtaBand } from '../components/ui/CtaBand';
import { SectionHeading } from '../components/ui/SectionHeading';
import { Seal } from '../components/brand/Seal';
import { principles, testimonials } from '../data/site';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { useBrand } from '../contexts/BrandContext';
const standards = [{
  title: 'Clinician-written protocols only',
  detail: 'Nothing ships from a checkout. Every prescription is written by a clinician licensed in your state after reading your panel.'
}, {
  title: 'US-licensed pharmacies, named',
  detail: 'Republic dispenses through US 503A and 503B pharmacies and retail wholesalers. The filling pharmacy is on your documentation.'
}, {
  title: 'Published pricing',
  detail: 'Membership and medication are priced separately and shown before approval. Your rate is held twelve months.'
}, {
  title: 'Portable records',
  detail: 'Labs, protocols and notes download as one PDF. Take them to any provider, any time, at no cost.'
}];
export function About() {
  const {
    brand,
    text
  } = useBrand();
  useDocumentMeta({
    title: `About ${brand.name}`,
    description: text('Access, transparency, choice and control. Republic is a direct-pay clinical practice built to show its work.')
  });
  return <main data-id="element-281" data-mp="2dfcrw4s6bhtu">
      <PageHeader eyebrow={`About ${brand.name}`} title="A modern institution, not another storefront." intro="Republic was built on the belief that clinically serious care can also be transparent, fairly priced and genuinely accessible — and that patients should not have to choose between the three." data-id="element-282" data-mp="2detcaupzx5iz" />

      <section className="bg-cream" data-id="element-283" data-mp="2w75f1ikka8tn">
        <div className="mx-auto max-w-shell px-6 pb-20 pt-12 md:px-8 md:pb-28" data-id="element-284" data-mp="2w86z2cjk25ni">
          <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20" data-id="element-285" data-mp="2r8kk9yo2whcl">
            <div data-id="element-286" data-mp="1oumo5qatxek0">
              <p className="font-serif text-3xl leading-[1.2] text-ink md:text-[2.5rem]" data-id="element-287" data-mp="1ov679ac7u8zm">
                Most people do not lack motivation. They lack access, an honest price and someone who
                will read the whole panel.
              </p>
              <div className="mt-8 flex flex-col gap-5 text-base leading-relaxed text-ink-soft md:text-lg" data-id="element-288" data-mp="1xt855h0nklio">
                <p data-id="element-289" data-mp="22nvxxs2ow232">
                  The last decade of digital health optimised for speed to checkout. It produced
                  clinics where the medication arrives faster than the explanation, where the price
                  changes with the dose, and where nobody can tell you which pharmacy filled the
                  vial.
                </p>
                <p data-id="element-290" data-mp="2ljm13imcz4ij">
                  {text('Republic is organised the other way around. Data first, then a written protocol, then a price you can see before you approve it. The clinical standard is conventional; what is different is how much of it we show you.')}
                </p>
              </div>
            </div>
            <div className="flex items-start justify-center" data-id="element-291" data-mp="ewwqhovhzjor">
              <Seal className="h-56 w-56 text-brick/70" data-id="element-292" data-mp="evbtztpmhqo9" />
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-ink/10 bg-cream-deep text-ink" data-id="element-293" data-mp="9v7hsfvqzi3u">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-294" data-mp="50a6t3ewqyob">
          <SectionHeading eyebrow="Principles" tone="cream" title="Four commitments, in order." data-id="element-295" data-mp="12kwh8ost7a3c" />
          <ul className="mt-14 grid gap-10 md:grid-cols-2 lg:grid-cols-4 lg:gap-0 lg:divide-x lg:divide-ink/10" data-id="element-296" data-mp="xo5zlx1cw6ft">
            {principles.map(principle => <li key={principle.name} className="lg:px-8 lg:first:pl-0 lg:last:pr-0" data-id="element-297" data-mp="omq2us3gs2u2">
                <h3 className="font-serif text-3xl text-ink" data-id="element-298" data-mp="3deago2ymlpxd">{principle.name}</h3>
                <p className="mt-4 text-base leading-relaxed text-ink-soft" data-id="element-299" data-mp="3dc61gova14hs">{principle.detail}</p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-light" data-id="element-300" data-mp="1137cy04d26qa">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-28" data-id="element-301" data-mp="15upbkhiwvv83">
          <SectionHeading eyebrow="Clinical standards" title="What we hold ourselves to." data-id="element-302" data-mp="15w0iurbkfl3y" />
          <ul className="mt-12 grid gap-10 md:grid-cols-2 lg:gap-x-20" data-id="element-303" data-mp="r7mf8e6brsmc">
            {standards.map(standard => <li key={standard.title} className="rule pt-6" data-id="element-304" data-mp="w2jlxeg4tpw2">
                <h3 className="font-serif text-2xl leading-snug text-ink" data-id="element-305" data-mp="1jvrzkztgvfln">{standard.title}</h3>
                <p className="mt-3 max-w-lg text-base leading-relaxed text-ink-soft" data-id="element-306" data-mp="1os8ohw8hpxzs">
                  {text(standard.detail)}
                </p>
              </li>)}
          </ul>
        </div>
      </section>

      <section className="bg-cream-deep" data-id="element-307" data-mp="1etkk409fhhek">
        <div className="mx-auto max-w-shell px-6 py-20 md:px-8 md:py-24" data-id="element-308" data-mp="3vk9k15urykpj">
          <ul className="grid gap-12 md:grid-cols-2 md:gap-16" data-id="element-309" data-mp="3vmdte3bg0bx7">
            {testimonials.map(testimonial => <li key={testimonial.name} data-id="element-310" data-mp="3s1tvfx4ezkp5">
                <blockquote data-id="element-311" data-mp="3s0si1lzxem49">
                  <p className="font-serif text-2xl leading-[1.25] text-ink md:text-3xl" data-id="element-312" data-mp="3s46lyyo8n3dt">
                    “{text(testimonial.quote)}”
                  </p>
                  <footer className="rule mt-8 pt-5 text-sm text-ink-muted" data-id="element-313" data-mp="5tuauuk7fsp0">
                    <span className="text-ink" data-id="element-314" data-mp="v9bexcs1d1m">{testimonial.name}</span> · {testimonial.detail}
                  </footer>
                </blockquote>
              </li>)}
          </ul>
        </div>
      </section>

      <CtaBand data-id="element-315" data-mp="34du5gzexfybm" />
    </main>;
}